/**
 * rackView — wspólna karuzela wieszaków (PLAN-V2 §5.2).
 * Shop importuje stąd; Wardrobe i Shop konsumują ObjectLayer/InteractiveObject.
 * Czysty model jest unit-testowalny bez Phaser.
 */
import {
  ITEMS,
  type ItemDef,
  type OutfitSlot,
  type SwimSlot,
} from '@pieskowo/shared';
import type { ObjectDef, SpawnPlacement } from '../../interact/types.js';
import { MIN_OBJECT_SIZE } from '../../interact/types.js';
import { itemTextureKey } from '../../avatar/parseOutfit.js';
import type { OutfitSlots, SwimSlots } from '../../avatar/parseOutfit.js';

export type RackTabId = OutfitSlot | 'swim';

export const RACK_TABS: { id: RackTabId; labelPl: string; iconColor: number }[] = [
  { id: 'body', labelPl: 'Ubranka', iconColor: 0xff9ec8 },
  { id: 'hat', labelPl: 'Czapki', iconColor: 0x7ec8e3 },
  { id: 'bow', labelPl: 'Kokardki', iconColor: 0xff6b6b },
  { id: 'glasses', labelPl: 'Okulary', iconColor: 0xbde0fe },
  { id: 'collar', labelPl: 'Obroże', iconColor: 0xffd700 },
  { id: 'shoes', labelPl: 'Buciki', iconColor: 0xc4a1ff },
  { id: 'accessory', labelPl: 'Dodatki', iconColor: 0xb8e6d0 },
  { id: 'swim', labelPl: 'Kąpiel', iconColor: 0x5dade2 },
];

/** Id ObjectDef dla wieszaka z itemem katalogu. */
export function rackDefId(itemId: string): string {
  return `rack_${itemId}`;
}

/** Wyciągnij itemId z defId wieszaka (null gdy nie-rack). */
export function itemIdFromRackDef(defId: string): string | null {
  if (!defId.startsWith('rack_')) return null;
  return defId.slice('rack_'.length);
}

export function itemsForTab(tab: RackTabId, catalog: readonly ItemDef[] = ITEMS): ItemDef[] {
  if (tab === 'swim') {
    return catalog.filter((i) => i.slot === 'suit' || i.slot === 'cap');
  }
  return catalog.filter((i) => i.slot === tab);
}

export type RackItemView = {
  item: ItemDef;
  owned: boolean;
  equipped: boolean;
  defId: string;
  priceLabel: string | null;
};

export function isItemEquipped(
  item: ItemDef,
  slots: OutfitSlots,
  swimSlots: SwimSlots,
): boolean {
  if (item.slot === 'suit' || item.slot === 'cap') {
    return swimSlots[item.slot as SwimSlot] === item.id;
  }
  return slots[item.slot as OutfitSlot] === item.id;
}

export function buildRackItems(
  tab: RackTabId,
  owned: ReadonlySet<string>,
  slots: OutfitSlots,
  swimSlots: SwimSlots,
  catalog: readonly ItemDef[] = ITEMS,
): RackItemView[] {
  return itemsForTab(tab, catalog).map((item) => {
    const isOwned = owned.has(item.id);
    return {
      item,
      owned: isOwned,
      equipped: isItemEquipped(item, slots, swimSlots),
      defId: rackDefId(item.id),
      priceLabel: isOwned ? null : `${item.priceZl} zł`,
    };
  });
}

/** ObjectDef wieszaka — drag+tap, placeholder gdy brak PNG. */
export function itemToRackObjectDef(item: ItemDef): ObjectDef {
  return {
    id: rackDefId(item.id),
    texture: itemTextureKey(item.id),
    // WAVE7-E: większe cele pod myszkę desktop (≥80)
    size: Math.max(MIN_OBJECT_SIZE + 16, 80),
    interactions: ['drag', 'tap'],
    fx: 'sparkle',
    dogReaction: 'happy',
    snapTargets: ['dog'],
  };
}

export function rackObjectDefs(items: readonly ItemDef[]): ObjectDef[] {
  return items.map(itemToRackObjectDef);
}

export type RackLayoutOpts = {
  /** Środkowy X pierwszego widocznego wieszaka. */
  originX: number;
  originY: number;
  /** Odstęp między wieszakami. */
  spacing?: number;
  /** Ile wieszaków na „stronie" karuzeli (widocznych). */
  pageSize?: number;
  /** Indeks strony (0-based). */
  page?: number;
};

export type RackLayout = {
  placements: SpawnPlacement[];
  page: number;
  pageCount: number;
  pageSize: number;
  visible: ItemDef[];
  total: number;
};

/**
 * Karuzela: stronicowanie wieszaków w poziomie.
 * pageSize domyślnie 5, spacing 110.
 */
export function layoutRackPage(
  items: readonly ItemDef[],
  opts: RackLayoutOpts,
): RackLayout {
  const pageSize = Math.max(1, opts.pageSize ?? 5);
  const spacing = opts.spacing ?? 110;
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const page = Math.min(Math.max(0, opts.page ?? 0), pageCount - 1);
  const start = page * pageSize;
  const visible = items.slice(start, start + pageSize);
  const placements: SpawnPlacement[] = visible.map((item, i) => ({
    defId: rackDefId(item.id),
    x: opts.originX + i * spacing,
    y: opts.originY,
  }));
  return { placements, page, pageCount, pageSize, visible, total };
}

/** Zastosuj item do slotów (toggle gdy już założony). */
export function applyEquip(
  item: ItemDef,
  slots: OutfitSlots,
  swimSlots: SwimSlots,
  mode: 'toggle' | 'force' = 'toggle',
): { slots: OutfitSlots; swimSlots: SwimSlots; mode: 'normal' | 'swim' } {
  if (item.slot === 'suit' || item.slot === 'cap') {
    const slot = item.slot as SwimSlot;
    const next =
      mode === 'force'
        ? item.id
        : swimSlots[slot] === item.id
          ? null
          : item.id;
    return {
      slots: { ...slots },
      swimSlots: { ...swimSlots, [slot]: next },
      mode: 'swim',
    };
  }
  const slot = item.slot as OutfitSlot;
  const next =
    mode === 'force' ? item.id : slots[slot] === item.id ? null : item.id;
  return {
    slots: { ...slots, [slot]: next },
    swimSlots: { ...swimSlots },
    mode: 'normal',
  };
}

/** Zdejmij slot (lustro). */
export function clearSlot(
  tab: RackTabId,
  slots: OutfitSlots,
  swimSlots: SwimSlots,
): { slots: OutfitSlots; swimSlots: SwimSlots } {
  if (tab === 'swim') {
    return { slots: { ...slots }, swimSlots: { suit: null, cap: null } };
  }
  return {
    slots: { ...slots, [tab]: null },
    swimSlots: { ...swimSlots },
  };
}

/** Czy drop (x,y) jest na psie (promień). */
export function hitDogForEquip(
  dropX: number,
  dropY: number,
  dogX: number,
  dogY: number,
  radius = 90,
): boolean {
  const dx = dropX - dogX;
  const dy = dropY - dogY;
  return dx * dx + dy * dy <= radius * radius;
}

/** Katalog wieszaków sklepu: wszystkie itemy lub filtr. */
export type ShopFilter = OutfitSlot | SwimSlot | 'all' | 'swim';

export function shopCatalog(
  filter: ShopFilter,
  catalog: readonly ItemDef[] = ITEMS,
): ItemDef[] {
  if (filter === 'all') return [...catalog];
  if (filter === 'swim') {
    return catalog.filter((i) => i.slot === 'suit' || i.slot === 'cap');
  }
  return catalog.filter((i) => i.slot === filter);
}

/**
 * Phaser UI karuzeli — pasek stron + metki cen + spawn na ObjectLayer.
 * Scena woła rebuild() po zmianie zakładki/strony/owned.
 */
export type RackViewSceneHost = {
  add: Phaser.GameObjects.GameObjectFactory;
  tweens: Phaser.Tweens.TweenManager;
  time: Phaser.Time.Clock;
};

export type RackViewOpts = {
  scene: Phaser.Scene;
  /** Pozycja startu wieszaków. */
  originX: number;
  originY: number;
  pageSize?: number;
  spacing?: number;
  depth?: number;
  onPageChange?: (page: number, pageCount: number) => void;
};

export class RackView {
  private readonly scene: Phaser.Scene;
  private readonly originX: number;
  private readonly originY: number;
  private readonly pageSize: number;
  private readonly spacing: number;
  private readonly depth: number;
  private readonly onPageChange?: (page: number, pageCount: number) => void;

  private page = 0;
  private pageCount = 1;
  private overlay: Phaser.GameObjects.Container | null = null;
  private navPrev: Phaser.GameObjects.Container | null = null;
  private navNext: Phaser.GameObjects.Container | null = null;
  private lastLayout: RackLayout | null = null;
  private lastViews: RackItemView[] = [];

  constructor(opts: RackViewOpts) {
    this.scene = opts.scene;
    this.originX = opts.originX;
    this.originY = opts.originY;
    this.pageSize = opts.pageSize ?? 5;
    this.spacing = opts.spacing ?? 110;
    this.depth = opts.depth ?? 50;
    this.onPageChange = opts.onPageChange;
  }

  get currentPage(): number {
    return this.page;
  }

  get pages(): number {
    return this.pageCount;
  }

  get layout(): RackLayout | null {
    return this.lastLayout;
  }

  get views(): RackItemView[] {
    return this.lastViews;
  }

  /**
   * Przelicz stronę + metki. Zwraca layout (placements/defs do ObjectLayer).
   * Scena sama respawnuje ObjectLayer na podstawie layout.placements.
   */
  compute(
    tab: RackTabId,
    owned: ReadonlySet<string>,
    slots: OutfitSlots,
    swimSlots: SwimSlots,
    catalog: readonly ItemDef[] = ITEMS,
  ): { views: RackItemView[]; layout: RackLayout; defs: ObjectDef[] } {
    const views = buildRackItems(tab, owned, slots, swimSlots, catalog);
    return this.computeFromViews(views);
  }

  /**
   * Karuzela z dowolnej listy itemów (filtr „Wszystko” w sklepie).
   */
  computeFromItems(
    items: readonly ItemDef[],
    owned: ReadonlySet<string>,
    slots: OutfitSlots,
    swimSlots: SwimSlots,
  ): { views: RackItemView[]; layout: RackLayout; defs: ObjectDef[] } {
    const views: RackItemView[] = items.map((item) => {
      const isOwned = owned.has(item.id);
      return {
        item,
        owned: isOwned,
        equipped: isItemEquipped(item, slots, swimSlots),
        defId: rackDefId(item.id),
        priceLabel: isOwned ? null : `${item.priceZl} zł`,
      };
    });
    return this.computeFromViews(views);
  }

  private computeFromViews(
    views: RackItemView[],
  ): { views: RackItemView[]; layout: RackLayout; defs: ObjectDef[] } {
    const items = views.map((v) => v.item);
    const tmp = layoutRackPage(items, {
      originX: this.originX,
      originY: this.originY,
      pageSize: this.pageSize,
      spacing: this.spacing,
      page: this.page,
    });
    this.page = tmp.page;
    this.pageCount = tmp.pageCount;
    const layout = layoutRackPage(items, {
      originX: this.originX,
      originY: this.originY,
      pageSize: this.pageSize,
      spacing: this.spacing,
      page: this.page,
    });
    this.lastLayout = layout;
    this.lastViews = views;
    const defs = rackObjectDefs(items);
    return { views, layout, defs };
  }

  /** Narysuj metki cen + strzałki stron (bez wieszaków — te z ObjectLayer). */
  paintOverlays(views: RackItemView[], layout: RackLayout): void {
    this.clearOverlays();
    const nodes: Phaser.GameObjects.GameObject[] = [];

    // wieszak-ramka + metka
    layout.visible.forEach((item, i) => {
      const x = layout.placements[i]!.x;
      const y = layout.placements[i]!.y;
      const view = views.find((v) => v.item.id === item.id);
      // hak wieszaka (placeholder)
      const hook = this.scene.add
        .rectangle(x, y - 48, 8, 28, 0xc4a882, 0.95)
        .setDepth(this.depth - 1);
      const bar = this.scene.add
        .ellipse(x, y - 36, 56, 14, 0xd4b896, 0.9)
        .setStrokeStyle(2, 0xffffff, 0.7)
        .setDepth(this.depth - 1);
      nodes.push(hook, bar);

      if (view && !view.owned && view.priceLabel) {
        const tagBg = this.scene.add
          .rectangle(x + 28, y + 42, 72, 28, 0xfff3b0, 0.98)
          .setStrokeStyle(2, 0xe6b800)
          .setDepth(this.depth + 5);
        const tag = this.scene.add
          .text(x + 28, y + 42, view.priceLabel, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '14px',
            color: '#4a3f55',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(this.depth + 6);
        nodes.push(tagBg, tag);
      } else if (view?.equipped) {
        const check = this.scene.add
          .text(x + 30, y - 30, '✓', {
            fontSize: '22px',
            color: '#27ae60',
          })
          .setOrigin(0.5)
          .setDepth(this.depth + 6);
        nodes.push(check);
      }
    });

    // nawigacja stron
    if (layout.pageCount > 1) {
      this.navPrev = this.makeNav(
        this.originX - 70,
        this.originY,
        '◀',
        () => this.setPage(this.page - 1),
        this.page > 0,
      );
      this.navNext = this.makeNav(
        this.originX + (layout.pageSize - 1) * this.spacing + 70,
        this.originY,
        '▶',
        () => this.setPage(this.page + 1),
        this.page < layout.pageCount - 1,
      );
      nodes.push(this.navPrev, this.navNext);
      const pageLabel = this.scene.add
        .text(
          this.originX + ((layout.pageSize - 1) * this.spacing) / 2,
          this.originY + 70,
          `${layout.page + 1} / ${layout.pageCount}`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '16px',
            color: '#6b5f7a',
          },
        )
        .setOrigin(0.5)
        .setDepth(this.depth + 5);
      nodes.push(pageLabel);
    }

    this.overlay = this.scene.add.container(0, 0, nodes);
    this.overlay.setDepth(this.depth);
  }

  setPage(page: number): void {
    const next = Math.min(Math.max(0, page), Math.max(0, this.pageCount - 1));
    if (next === this.page) return;
    this.page = next;
    this.onPageChange?.(this.page, this.pageCount);
  }

  private makeNav(
    x: number,
    y: number,
    label: string,
    onClick: () => void,
    enabled: boolean,
  ): Phaser.GameObjects.Container {
    const size = MIN_OBJECT_SIZE;
    const bg = this.scene.add
      .rectangle(0, 0, size, size, enabled ? 0xffe4f0 : 0xe0d8e8, 0.95)
      .setStrokeStyle(3, 0xffffff)
      .setInteractive({ useHandCursor: enabled });
    const t = this.scene.add
      .text(0, 0, label, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: enabled ? '#4a3f55' : '#a89bb0',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const c = this.scene.add.container(x, y, [bg, t]);
    c.setSize(size, size);
    c.setDepth(this.depth + 10);
    if (enabled) {
      bg.on('pointerdown', onClick);
    }
    return c;
  }

  clearOverlays(): void {
    this.overlay?.destroy(true);
    this.overlay = null;
    this.navPrev = null;
    this.navNext = null;
  }

  destroy(): void {
    this.clearOverlays();
    this.lastLayout = null;
    this.lastViews = [];
  }
}
